import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import InputAdornment from "@mui/material/InputAdornment";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import CustomDatePicker from "../../ui/customDatePicker/customDatePicker.component";
import CustomAutocomplete from "../../ui/customAutocomplete/customAutocomplete.component";
import CustomModal from "../../ui/modal/modal.component";
import EquipmentCounterForm from "../../ui/equipmentCounterForm/equipmentCounterForm.component";
import {
  CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION,
  LowCounterValueConfirmModal,
  LowCounterValueWarning,
  parseCounterNumericValue,
} from "../../ui/equipmentCounterForm/lowCounterValueWarning.component";
import InboxIcon from "@mui/icons-material/Inbox";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import axios from "../../../api/axios";
import { useHasPermission } from "../../../contexts/auth";
import { Contract, ContractTerm } from "../../../types/contracts";
import {
  CONTRACT_TERMS_ENDPOINT,
  CONTRACTS_ENDPOINT,
  SETTLEMENTS_ENDPOINT,
  SETTLEMENTS_PENDING_ACTIONS_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
  EQUIPMENT_COUNTERS_ENDPOINT,
  EQUIPMENT_COUNTERS_VERIFY_ENDPOINT,
} from "../../../consts";
import CustomCircularProgress from "../../ui/customCircularProgress/customCircularProgress.component";
import {
  SettlementExtraPosition,
  SettlementPending,
  SettlementPendingPosition,
} from "../../../types/settlements";
import {
  formatCounterInputValue,
  formatCounterProvider,
  formatDateTime,
  normalizeCounterInputValue,
} from "../../../utils";
import {
  getContractTermEquipmentLabel,
  getEquipmentIdForPendingPosition,
  getSettlementClientName,
  getSettlementContractNumber,
  getSettlementEquipmentLabel,
  getSettlementEquipments,
  isCommonCounterSettlement,
} from "../../../utils/settlementContractTerms";
import { CounterDetailsTable } from "./counterDetailsTable.component";
import styles from "./settlementCreator.module.css";

interface SettlementCreatorProps {
  contractId?: string;
  onSuccess?: () => void;
  formatTermDisplay?: (term: ContractTerm) => string;
}

const PENDING_SETTLEMENTS_PAGE_SIZE = 10;

interface SettlementExtraPositionFormRow {
  tempId: string;
  id?: number;
  name: string;
  price: string;
}

export const SettlementCreator = ({ contractId, onSuccess, formatTermDisplay }: SettlementCreatorProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const canEnterEveryCounterValue = useHasPermission(CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION);
  const [lowValueConfirmOpen, setLowValueConfirmOpen] = useState<boolean>(false);
  const [lowValueWarnings, setLowValueWarnings] = useState<LowCounterValueWarning[]>([]);
  const pendingBulkSettlementRef = useRef<SettlementPending | null>(null);
  const [editingCounterId, setEditingCounterId] = useState<string | null>(null);
  const [contractTerms, setContractTerms] = useState<ContractTerm[]>([]);
  const [loadingTerms, setLoadingTerms] = useState<boolean>(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [manualGenerating, setManualGenerating] = useState<boolean>(false);
  const [pendingSettlements, setPendingSettlements] = useState<SettlementPending[]>([]);
  const [loadingPending, setLoadingPending] = useState<boolean>(false);
  const [loadingPendingMore, setLoadingPendingMore] = useState<boolean>(false);
  const [pendingPage, setPendingPage] = useState<number>(1);
  const [hasMorePending, setHasMorePending] = useState<boolean>(false);
  const [pendingTotalCount, setPendingTotalCount] = useState<number>(0);
  const [pendingSearchInput, setPendingSearchInput] = useState<string>("");
  const [pendingSearch, setPendingSearch] = useState<string>("");
  const [pendingContractNumberSort, setPendingContractNumberSort] = useState<"asc" | "desc">("desc");
  const [activeTab, setActiveTab] = useState<number>(0);
  const [verifyingCounters, setVerifyingCounters] = useState<boolean>(false);
  const [counterValues, setCounterValues] = useState<Record<string, string>>({});
  const [bulkCounterSubmitting, setBulkCounterSubmitting] = useState<Record<string, boolean>>(
    {}
  );
  const [confirmValues, setConfirmValues] = useState<
    Record<string, { saleDate: string; totalOverridden: string }>
  >({});
  const [confirmSubmitting, setConfirmSubmitting] = useState<Record<string, boolean>>(
    {}
  );
  const [extraPositionValues, setExtraPositionValues] = useState<
    Record<string, SettlementExtraPositionFormRow[]>
  >({});
  const [extraPositionSubmitting, setExtraPositionSubmitting] = useState<
    Record<string, boolean>
  >({});
  const [extraPositionErrors, setExtraPositionErrors] = useState<
    Record<string, string>
  >({});
  const pendingScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  const isContractLocked = Boolean(contractId);
  const selectedContractId = selectedContract?.id ?? contractId ?? null;
  const hasPendingForSelectedContract = Boolean(selectedContractId && pendingTotalCount > 0);

  const defaultSaleDate = useMemo(
    () => dayjs().format("YYYY-MM-DD"),
    []
  );

  const createExtraPositionRow = useCallback(
    (position?: SettlementExtraPosition): SettlementExtraPositionFormRow => ({
      tempId: `extra-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      id: position?.id,
      name: position?.name || "",
      price:
        position?.price === null || position?.price === undefined
          ? ""
          : String(position.price),
    }),
    []
  );

  const buildExtraPositionRows = useCallback(
    (positions?: SettlementExtraPosition[]): SettlementExtraPositionFormRow[] => {
      if (!positions || positions.length === 0) {
        return [];
      }

      return positions.map((position) => createExtraPositionRow(position));
    },
    [createExtraPositionRow]
  );

  useEffect(() => {
    if (!contractId) {
      return;
    }

    const fetchContract = async () => {
      try {
        const response = await axios.get(`${CONTRACTS_ENDPOINT}${contractId}/`);
        setSelectedContract(response.data);
      } catch (error) {
        enqueueSnackbar(t("Failed to fetch contract"), ERROR_SNACKBAR);
      }
    };

    fetchContract();
  }, [contractId, enqueueSnackbar, t]);

  useEffect(() => {
    const fetchContractTerms = async () => {
      if (!selectedContractId) {
        setContractTerms([]);
        return;
      }

      setLoadingTerms(true);
      try {
        const response = await axios.get(CONTRACT_TERMS_ENDPOINT, {
          params: { contract: selectedContractId },
        });
        const terms = response.data.results || [];
        setContractTerms(terms);
      } catch (error) {
        enqueueSnackbar(t("Failed to fetch equipment"), ERROR_SNACKBAR);
      } finally {
        setLoadingTerms(false);
      }
    };

    fetchContractTerms();
  }, [selectedContractId, enqueueSnackbar, t]);

  const fetchPendingSettlements = useCallback(async (page = 1, append = false) => {
    if (append) {
      setLoadingPendingMore(true);
    } else {
      setLoadingPending(true);
    }

    try {
      const params = {
        ...(selectedContractId ? { contract_id: selectedContractId } : {}),
        ...(pendingSearch ? { search: pendingSearch } : {}),
        contract_number_sort: pendingContractNumberSort,
        page,
        page_size: PENDING_SETTLEMENTS_PAGE_SIZE,
      };
      const response = await axios.get(SETTLEMENTS_PENDING_ACTIONS_ENDPOINT, { params });
      const payload = response.data;
      const settlements = Array.isArray(payload) ? payload : payload?.results || [];
      const totalCount = Array.isArray(payload)
        ? settlements.length
        : Number(payload?.count ?? settlements.length);
      const hasNextPage = Array.isArray(payload) ? false : Boolean(payload?.next);

      setPendingSettlements((prev) => (append ? [...prev, ...settlements] : settlements));
      setPendingPage(page);
      setHasMorePending(hasNextPage);
      setPendingTotalCount(totalCount);

      return { settlements, totalCount, hasNextPage };
    } catch (error) {
      enqueueSnackbar(t("Failed to fetch settlements"), ERROR_SNACKBAR);
    } finally {
      if (append) {
        setLoadingPendingMore(false);
      } else {
        setLoadingPending(false);
      }
    }
    return { settlements: [], totalCount: 0, hasNextPage: false };
  }, [selectedContractId, pendingSearch, pendingContractNumberSort, enqueueSnackbar, t]);

  useEffect(() => {
    fetchPendingSettlements(1, false);
  }, [fetchPendingSettlements]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPendingSearch(pendingSearchInput.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [pendingSearchInput]);

  useEffect(() => {
    const settlementIds = new Set(pendingSettlements.map((settlement) => settlement.id));

    setExtraPositionValues((prev) => {
      const next: Record<string, SettlementExtraPositionFormRow[]> = {};

      pendingSettlements.forEach((settlement) => {
        if (prev[settlement.id]) {
          next[settlement.id] = prev[settlement.id];
          return;
        }

        next[settlement.id] = buildExtraPositionRows(settlement.extra_positions);
      });

      return next;
    });

    setExtraPositionSubmitting((prev) => {
      const next: Record<string, boolean> = {};
      Object.entries(prev).forEach(([settlementId, value]) => {
        if (settlementIds.has(settlementId)) {
          next[settlementId] = value;
        }
      });
      return next;
    });

    setExtraPositionErrors((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([settlementId, value]) => {
        if (settlementIds.has(settlementId)) {
          next[settlementId] = value;
        }
      });
      return next;
    });

  }, [pendingSettlements, buildExtraPositionRows]);

  useEffect(() => {
    if (pendingTotalCount > 0) {
      setActiveTab(1);
    }
  }, [pendingTotalCount]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchPendingSettlements(1, false);
    };

    window.addEventListener("settlementAdded", handleRefresh);
    window.addEventListener("settlementUpdated", handleRefresh);
    window.addEventListener("equipmentCounterAdded", handleRefresh);

    return () => {
      window.removeEventListener("settlementAdded", handleRefresh);
      window.removeEventListener("settlementUpdated", handleRefresh);
      window.removeEventListener("equipmentCounterAdded", handleRefresh);
    };
  }, [fetchPendingSettlements]);

  useEffect(() => {
    if (activeTab !== 1 || !hasMorePending || loadingPending || loadingPendingMore) {
      return;
    }

    const trigger = loadMoreTriggerRef.current;
    if (!trigger) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting && !loadingPendingMore && !loadingPending) {
          fetchPendingSettlements(pendingPage + 1, true);
        }
      },
      {
        root: pendingScrollContainerRef.current,
        rootMargin: "200px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(trigger);

    return () => {
      observer.disconnect();
    };
  }, [
    activeTab,
    hasMorePending,
    loadingPending,
    loadingPendingMore,
    pendingPage,
    fetchPendingSettlements,
  ]);

  const handleGenerateManual = async () => {
    if (!selectedContractId) {
      enqueueSnackbar(t("Please select a contract"), ERROR_SNACKBAR);
      return;
    }
    if (contractTerms.length === 0) {
      enqueueSnackbar(t("No equipment available"), ERROR_SNACKBAR);
      return;
    }

    setManualGenerating(true);
    const results = await Promise.allSettled(
      contractTerms.map((term) =>
        axios.post(SETTLEMENTS_ENDPOINT, { contract_term_id: term.id })
      )
    );

    const successCount = results.filter((result) => result.status === "fulfilled").length;
    const failureCount = results.length - successCount;

    if (successCount > 0) {
      enqueueSnackbar(t("Settlements generated successfully"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementAdded"));
      setActiveTab(1);
    }

    if (failureCount > 0) {
      enqueueSnackbar(t("Some settlements failed to generate"), ERROR_SNACKBAR);
    }

    setManualGenerating(false);
  };

  const defaultFormatTermDisplay = (term: ContractTerm): string => {
    return getContractTermEquipmentLabel(term, term.id);
  };

  const formatDisplay = formatTermDisplay || defaultFormatTermDisplay;

  const getMissingCounterPositions = (positions: SettlementPendingPosition[] = []) =>
    positions.filter((position) => !position.counter_id);

  const getOutdatedCounterPositions = (positions: SettlementPendingPosition[] = []) =>
    positions.filter(
      (position) =>
        position.counter_id &&
        (position.investigation_reasons || []).includes("O")
    );

  const hasInvestigationReasons = (positions: SettlementPendingPosition[] = []) =>
    positions.some((position) => (position.investigation_reasons || []).length > 0);

  const getUnverifiedCounterDetails = (positions: SettlementPendingPosition[] = []) =>
    positions.filter(
      (position) =>
        position.counter_id &&
        (position.investigation_reasons || []).includes("NV")
    );

  const getUnverifiedCounterIds = (positions: SettlementPendingPosition[] = []) => {
    return positions
      .filter((position) =>
        (position.investigation_reasons || []).includes("NV") && position.counter_id
      )
      .map((position) => position.counter_id as string);
  };

  const getDistinctInvestigationReasonLabels = (
    positions: SettlementPendingPosition[] = []
  ) => {
    const labels = positions
      .map((position) => position.investigation_reasons_display)
      .filter(Boolean) as string[];

    return Array.from(new Set(labels));
  };

  const canPrefillConfirm = (settlement: SettlementPending) => {
    const positions = settlement.positions || [];
    const hasCounterMissing = positions.some((position) => !position.counter_id);
    return settlement.status === "W" && !hasCounterMissing && !hasInvestigationReasons(positions);
  };

  const getPreviousCounterData = (positions: SettlementPendingPosition[] = []) => {
    return positions
      .filter((position) =>
        position.previous_counter_value !== null &&
        position.previous_counter_value !== undefined
      )
      .map((position) => ({
        id: position.counter_id ?? undefined,
        kind: position.counter_kind_display,
        value: position.counter_value as string | number,
        previousValue: position.previous_counter_value as string | number,
        difference: position.counter_difference as string | number,
        previousReadDate: position.previous_counter_read_at
          ? formatDateTime(position.previous_counter_read_at)
          : "-",
        currentReadDate: position.counter_read_at ? formatDateTime(position.counter_read_at) : "-",
        previousProvider: formatCounterProvider(
          position.previous_counter_provider_display,
          position.previous_counter_email_source_display,
        ),
        previousProviderCode: position.previous_counter_provider || "-",
        currentProvider: formatCounterProvider(
          position.counter_provider_display,
          position.counter_email_source_display,
        ),
        currentProviderCode: position.counter_provider || "-",
      }));
  };

  const getUnverifiedCounterTableData = (positions: SettlementPendingPosition[] = []) => {
    return positions
      .filter((position) =>
        position.counter_id &&
        (position.investigation_reasons || []).includes("NV")
      )
      .map((position) => ({
        id: position.counter_id as string,
        kind: position.counter_kind_display,
        value: (position.counter_value ?? "-") as number | string,
        previousValue: (position.previous_counter_value ?? "-") as number | string,
        difference: (position.counter_difference ?? "-") as number | string,
        previousReadDate: position.previous_counter_read_at
          ? formatDateTime(position.previous_counter_read_at)
          : "-",
        currentReadDate: position.counter_read_at ? formatDateTime(position.counter_read_at) : "-",
        previousProvider: formatCounterProvider(
          position.previous_counter_provider_display,
          position.previous_counter_email_source_display,
        ),
        previousProviderCode: position.previous_counter_provider || "-",
        currentProvider: formatCounterProvider(
          position.counter_provider_display,
          position.counter_email_source_display,
        ),
        currentProviderCode: position.counter_provider || "-",
      }));
  };

  const getCounterKey = (settlementId: string, positionId?: string) =>
    `${settlementId}-${positionId || "unknown"}`;

  interface EquipmentPositionGroup {
    equipmentId: string;
    equipmentName: string;
    positions: SettlementPendingPosition[];
  }

  const groupPositionsByEquipment = (
    positions: SettlementPendingPosition[] = []
  ): EquipmentPositionGroup[] => {
    const groups = new Map<string, EquipmentPositionGroup>();

    for (const position of positions) {
      const equipmentId =
        position.equipment_id != null ? String(position.equipment_id) : "unknown";
      const existing = groups.get(equipmentId);

      if (existing) {
        existing.positions.push(position);
        continue;
      }

      groups.set(equipmentId, {
        equipmentId,
        equipmentName: position.equipment_full_name || equipmentId,
        positions: [position],
      });
    }

    return Array.from(groups.values());
  };

  const getSettlementTitle = (settlement: SettlementPending) => {
    if (isCommonCounterSettlement(settlement)) {
      return t("Common counter for {{count}} equipments", {
        count: getSettlementEquipments(settlement).length,
      });
    }
    return getSettlementEquipmentLabel(settlement, t("Settlement"));
  };

  const renderCounterInput = (
    settlement: SettlementPending,
    position: SettlementPendingPosition
  ) => {
    const key = getCounterKey(settlement.id, position.id);

    return (
      <Stack
        key={`${settlement.id}-${position.id}-input`}
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ width: "100%" }}
      >
        <TextField
          label={position.counter_kind_display || t("Counter value")}
          size="small"
          value={formatCounterInputValue(counterValues[key] || "")}
          onChange={(event) => {
            handleCounterValueChange(settlement.id, position.id, event.target.value);
          }}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          sx={{ flex: 1, minWidth: { xs: "100%", sm: 220 } }}
        />
      </Stack>
    );
  };

  const getExtraPositions = (settlementId: string) =>
    extraPositionValues[settlementId] || [];

  const setSettlementExtraPositionError = (settlementId: string, value = "") => {
    setExtraPositionErrors((prev) => ({
      ...prev,
      [settlementId]: value,
    }));
  };

  const normalizeExtraPositionsPayload = (positions: SettlementExtraPositionFormRow[]) => {
    const normalized: SettlementExtraPosition[] = [];

    for (const position of positions) {
      const name = position.name.trim();
      const rawPrice = position.price.trim();

      if (!name && !rawPrice) {
        continue;
      }

      if (!name || !rawPrice) {
        return {
          isValid: false,
          error: t("Please complete both name and price for each extra position"),
          payload: [] as SettlementExtraPosition[],
        };
      }

      const normalizedPrice = rawPrice.replace(",", ".");
      if (!/^\d+(\.\d{1,2})?$/.test(normalizedPrice)) {
        return {
          isValid: false,
          error: t("Invalid extra position price"),
          payload: [] as SettlementExtraPosition[],
        };
      }

      normalized.push({
        ...(position.id ? { id: position.id } : {}),
        name,
        price: normalizedPrice,
      });
    }

    return {
      isValid: true,
      error: "",
      payload: normalized,
    };
  };

  const handleAddExtraPositionRow = (settlementId: string) => {
    setSettlementExtraPositionError(settlementId);
    setExtraPositionValues((prev) => ({
      ...prev,
      [settlementId]: [...(prev[settlementId] || []), createExtraPositionRow()],
    }));
  };

  const handleRemoveExtraPositionRow = (settlementId: string, rowTempId: string) => {
    setSettlementExtraPositionError(settlementId);
    setExtraPositionValues((prev) => ({
      ...prev,
      [settlementId]: (prev[settlementId] || []).filter(
        (position) => position.tempId !== rowTempId
      ),
    }));
  };

  const handleExtraPositionValueChange = (
    settlementId: string,
    rowTempId: string,
    field: "name" | "price",
    value: string
  ) => {
    setSettlementExtraPositionError(settlementId);
    setExtraPositionValues((prev) => ({
      ...prev,
      [settlementId]: (prev[settlementId] || []).map((position) =>
        position.tempId === rowTempId
          ? {
              ...position,
              [field]: value,
            }
          : position
      ),
    }));
  };

  const handleCounterValueChange = (
    settlementId: string,
    positionId: string | undefined,
    value: string
  ) => {
    const key = getCounterKey(settlementId, positionId);
    const normalizedValue = normalizeCounterInputValue(value);
    if (normalizedValue === null) {
      return;
    }
    setCounterValues((prev) => ({ ...prev, [key]: normalizedValue }));
  };

  const handleConfirmValueChange = (
    settlementId: string,
    field: "saleDate" | "totalOverridden",
    value: string
  ) => {
    setConfirmValues((prev) => ({
      ...prev,
      [settlementId]: {
        saleDate: prev[settlementId]?.saleDate || defaultSaleDate,
        totalOverridden: prev[settlementId]?.totalOverridden || "",
        [field]: value,
      },
    }));
  };

  const getBulkCounterRowsToSubmit = (settlement: SettlementPending) => {
    const positions = settlement.positions || [];
    const actionPositions = [
      ...getMissingCounterPositions(positions),
      ...getOutdatedCounterPositions(positions),
    ];

    return actionPositions
      .map((position) => ({
        position,
        key: getCounterKey(settlement.id, position.id),
        equipmentId: getEquipmentIdForPendingPosition(settlement, positions.indexOf(position)),
      }))
      .filter(({ key }) => (counterValues[key] || "").trim() !== "");
  };

  const performBulkAddCounterValues = async (
    settlement: SettlementPending,
    rowsToSubmit: ReturnType<typeof getBulkCounterRowsToSubmit>
  ) => {
    setBulkCounterSubmitting((prev) => ({ ...prev, [settlement.id]: true }));
    try {
      for (const { position, key, equipmentId } of rowsToSubmit) {
        await axios.post(EQUIPMENT_COUNTERS_ENDPOINT, {
          equipment_id: equipmentId,
          provider: "U",
          read_at: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
          kind: position.counter_kind,
          value: counterValues[key],
          verify: true,
        });
      }
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlement.id}/refresh-calculation/`);
      enqueueSnackbar(t("Counter values saved and verified"), SUCCESS_SNACKBAR);
      setCounterValues((prev) => {
        const next = { ...prev };
        rowsToSubmit.forEach(({ key }) => delete next[key]);
        return next;
      });
      window.dispatchEvent(new Event("equipmentCounterAdded"));
    } catch (error) {
      const message = (error as any)?.response?.data?.message || t("Failed to add counter value");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setBulkCounterSubmitting((prev) => ({ ...prev, [settlement.id]: false }));
    }
  };

  const requestBulkAddCounterValues = (settlement: SettlementPending) => {
    const rowsToSubmit = getBulkCounterRowsToSubmit(settlement);

    if (rowsToSubmit.length === 0) {
      enqueueSnackbar(t("Please enter at least one counter value"), ERROR_SNACKBAR);
      return;
    }

    if (rowsToSubmit.some(({ position, equipmentId }) => !equipmentId || !position.counter_kind)) {
      enqueueSnackbar(t("Equipment is required to add counter"), ERROR_SNACKBAR);
      return;
    }

    if (canEnterEveryCounterValue) {
      const warnings: LowCounterValueWarning[] = rowsToSubmit
        .map(({ position, key }): LowCounterValueWarning | null => {
          const enteredValue = parseCounterNumericValue(counterValues[key]);
          const latestValue = parseCounterNumericValue(position.previous_counter_value);
          if (enteredValue === null || latestValue === null || enteredValue >= latestValue) {
            return null;
          }
          return {
            key,
            label: position.counter_kind_display,
            enteredValue,
            latestValue,
            latestDate: position.previous_counter_read_at
              ? formatDateTime(position.previous_counter_read_at)
              : undefined,
          };
        })
        .filter((warning): warning is LowCounterValueWarning => warning !== null);

      if (warnings.length > 0) {
        setLowValueWarnings(warnings);
        pendingBulkSettlementRef.current = settlement;
        setLowValueConfirmOpen(true);
        return;
      }
    }

    void performBulkAddCounterValues(settlement, rowsToSubmit);
  };

  const handleConfirmLowValueBulkAdd = () => {
    const settlement = pendingBulkSettlementRef.current;
    pendingBulkSettlementRef.current = null;
    if (!settlement) {
      return;
    }
    void performBulkAddCounterValues(settlement, getBulkCounterRowsToSubmit(settlement));
  };

  const handleConfirmSettlement = async (settlement: SettlementPending) => {
    const values = confirmValues[settlement.id] || {
      saleDate: defaultSaleDate,
      totalOverridden: "",
    };

    if (!values.saleDate) {
      enqueueSnackbar(t("Sale date is required"), ERROR_SNACKBAR);
      return;
    }

    setConfirmSubmitting((prev) => ({ ...prev, [settlement.id]: true }));
    try {
      await axios.patch(`${SETTLEMENTS_ENDPOINT}${settlement.id}/`, {
        status: "C",
        sale_date: values.saleDate,
        total_overridden: values.totalOverridden || null,
      });
      enqueueSnackbar(t("Settlement confirmed"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      window.dispatchEvent(new Event("settlementConfirmed"));
      const remaining = await fetchPendingSettlements(1, false);
      if (remaining.totalCount === 0) {
        const contractId =
          selectedContractId ??
          (settlement.contract_term?.contract != null
            ? String(settlement.contract_term.contract)
            : undefined);
        if (contractId) {
          onSuccess?.();
          navigate(`/contracts/${contractId}/sale-summary/${values.saleDate}`);
        }
      }
    } catch (error) {
      enqueueSnackbar(t("Failed to confirm settlement"), ERROR_SNACKBAR);
    } finally {
      setConfirmSubmitting((prev) => ({ ...prev, [settlement.id]: false }));
    }
  };

  const handleVerifyCounters = async (settlementId: string, counterIds: string[]) => {
    if (counterIds.length === 0) {
      return;
    }

    setVerifyingCounters(true);
    try {
      await axios.post(EQUIPMENT_COUNTERS_VERIFY_ENDPOINT, { counter_ids: counterIds });
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlementId}/refresh-calculation/`);
      enqueueSnackbar(t("Counters marked as verified"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("equipmentCounterAdded"));
    } catch (error) {
      enqueueSnackbar(t("Failed to verify counters"), ERROR_SNACKBAR);
    } finally {
      setVerifyingCounters(false);
    }
  };

  const handleDeleteSettlement = async (settlementId: string) => {
    try {
      await axios.delete(`${SETTLEMENTS_ENDPOINT}${settlementId}/`);
      enqueueSnackbar(t("Settlement deleted"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      await fetchPendingSettlements(1, false);
    } catch (error) {
      enqueueSnackbar(t("Failed to delete settlement"), ERROR_SNACKBAR);
    }
  };

  const handleSaveExtraPositions = async (settlement: SettlementPending) => {
    const settlementId = settlement.id;
    const positions = getExtraPositions(settlementId);
    const normalized = normalizeExtraPositionsPayload(positions);

    if (!normalized.isValid) {
      setSettlementExtraPositionError(settlementId, normalized.error);
      return;
    }

    setExtraPositionSubmitting((prev) => ({ ...prev, [settlementId]: true }));
    setSettlementExtraPositionError(settlementId);

    try {
      const response = await axios.patch(`${SETTLEMENTS_ENDPOINT}${settlementId}/`, {
        extra_positions: normalized.payload,
      });

      const savedExtraPositions = response.data?.extra_positions || [];

      setExtraPositionValues((prev) => ({
        ...prev,
        [settlementId]: buildExtraPositionRows(savedExtraPositions),
      }));

      setPendingSettlements((prev) =>
        prev.map((item) =>
          item.id === settlementId
            ? {
                ...item,
                extra_positions: savedExtraPositions,
              }
            : item
        )
      );

      enqueueSnackbar(t("Extra positions saved"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
    } catch (error) {
      const responseData = (error as any)?.response?.data;
      const message =
        responseData?.message ||
        (typeof responseData === "string" ? responseData : t("Failed to save extra positions"));

      setSettlementExtraPositionError(settlementId, message);
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setExtraPositionSubmitting((prev) => ({ ...prev, [settlementId]: false }));
    }
  };

  return (
    <Box className={styles.mainContainer}>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="fullWidth"
      >
        <Tab label={t("Manual generation")} />
        <Tab label={t("Pending settlements")} />
      </Tabs>

      {activeTab === 0 && (
        <Box className={styles.tabsContainer}>
        <CustomAutocomplete
          apiConfig={{
            url: CONTRACTS_ENDPOINT,
            minSearchChars: 2,
            debounceMs: 300,
          }}
          value={selectedContract}
          onChange={(_, value) => setSelectedContract(value)}
          getOptionLabel={(option) => {
            const labelParts = [option.number, option.client?.name].filter(Boolean);
            return labelParts.join(" - ") || String(option.id);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Select contract")}
              size="small"
            />
          )}
          disabled={isContractLocked}
        />

        <Box className={styles.tabContentContainer}>
          <Typography variant="subtitle2" color="text.secondary">
            {t("Next auto settlement dates")}
          </Typography>
          {loadingTerms ? (
            <CustomCircularProgress />
          ) : contractTerms.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("No equipment available")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {contractTerms.map((term) => (
                <Paper key={term.id} variant="outlined" className={styles.contractTermCard}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">
                      {formatDisplay(term)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("Next Settlement Date")}: {term.next_settlement_date || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("Settlement Cycle")}: {term.settlement_cycle_display || "-"}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>

        {hasPendingForSelectedContract && (
          <Alert severity="warning">
            {t("Resolve pending settlements before generating new ones")}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleGenerateManual}
          disabled={
            manualGenerating ||
            !selectedContractId ||
            contractTerms.length === 0 ||
            hasPendingForSelectedContract
          }
          fullWidth
        >
          {manualGenerating ? t("Processing...") : t("Generate settlements")}
        </Button>
      </Box>
      )}

      {activeTab === 1 && (
        <Box className={styles.tabsContainer}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            label={t("Search by machine serial, client, contract number")}
            size="small"
            value={pendingSearchInput}
            onChange={(event) => setPendingSearchInput(event.target.value)}
            fullWidth
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: pendingSearchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPendingSearchInput("");
                      setPendingSearch("");
                    }}
                    aria-label={t("Clear")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={pendingContractNumberSort}
            onChange={(_event, value: "asc" | "desc" | null) => {
              if (value !== null) {
                setPendingContractNumberSort(value);
              }
            }}
            aria-label={t("Ordering") + ":"}
          >
            <ToggleButton value="desc">{t("Newest number first")}</ToggleButton>
            <ToggleButton value="asc">{t("Oldest number first")}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {loadingPending && pendingSettlements.length === 0 ? (
          <CustomCircularProgress />
        ) : pendingSettlements.length === 0 ? (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            className={styles.noSettlementsBox}
            sx={{
              padding: 4,
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              color: "text.secondary",
            }}
          >
            <InboxIcon className={styles.noSettlementsIcon} />
            <Typography variant="subtitle1">{t("No pending settlements")}</Typography>
          </Stack>
        ) : (
          <Box ref={pendingScrollContainerRef} className={styles.pendingScrollContainer}>
          <Stack spacing={2}>
            {pendingSettlements.map((settlement) => {
              const positions = settlement.positions || [];
              const missingPositions = getMissingCounterPositions(positions);
              const outdatedPositions = getOutdatedCounterPositions(positions);
              const counterActionPositions = [...missingPositions, ...outdatedPositions];
              const hasInvestigation = hasInvestigationReasons(positions);
              const readyToConfirm = canPrefillConfirm(settlement);
              const unverifiedCounterIds = getUnverifiedCounterIds(positions);
              const unverifiedCounterDetails = getUnverifiedCounterDetails(positions);
              const previousCounterData = getPreviousCounterData(positions);
              const distinctInvestigationReasonLabels =
                getDistinctInvestigationReasonLabels(positions);
              const isCommonCounter = isCommonCounterSettlement(settlement);
              const bulkSaveButtons =
                counterActionPositions.length > 0 ? (
                  <Stack direction="row" spacing={1} className={styles.counterActionStack}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={bulkCounterSubmitting[settlement.id]}
                      onClick={() => requestBulkAddCounterValues(settlement)}
                    >
                      {bulkCounterSubmitting[settlement.id] ? t("Processing...") : t("Save")}
                    </Button>
                  </Stack>
                ) : null;

              return (
                <Paper key={settlement.id} variant="outlined" className={styles.settlementCard}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Stack spacing={0.5} flex={1}>
                        <Typography variant="subtitle1">
                          {getSettlementTitle(settlement)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("Contract")}: {getSettlementContractNumber(settlement)}
                          {" — "}
                          {t("Client")}: {getSettlementClientName(settlement)}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {t("Created at")}: {formatDateTime(settlement.created_at)}
                          </Typography>
                        </Stack>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSettlement(settlement.id)}
                        sx={{ color: "text.secondary" }}
                      >
                        <DeleteIcon/>
                      </IconButton>
                    </Stack>
                    {!isCommonCounter && previousCounterData.length > 0 && (
                        <Paper
                          variant="outlined"
                          className={styles.previousCounterSection}
                        >
                          <CounterDetailsTable
                            title={t("Previous counter")}
                            data={previousCounterData}
                            showEditButton
                            onEdit={(counterId) => setEditingCounterId(counterId)}
                          />
                        </Paper>
                      )}

                    <Divider />

                    <Paper
                      variant="outlined"
                      className={styles.counterDetailsSection}
                    >
                      <Stack spacing={2}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">
                            {t("Actions needed")}
                          </Typography>
                          <Stack direction="row" spacing={1} className={styles.counterActionStack}>
                            {!isCommonCounter &&
                              hasInvestigation &&
                              distinctInvestigationReasonLabels.map((reasonLabel) => (
                                  <Chip
                                    key={`${settlement.id}-${reasonLabel}`}
                                    label={reasonLabel}
                                    size="small"
                                  />
                                ))}
                            {readyToConfirm && (
                              <Chip label={t("Ready to confirm")} size="small" />
                            )}
                          </Stack>
                        </Stack>

                        {isCommonCounter ? (
                          <Stack spacing={1.5}>
                            {groupPositionsByEquipment(positions).map((equipmentGroup) => {
                              const groupMissingPositions = getMissingCounterPositions(
                                equipmentGroup.positions
                              );
                              const groupOutdatedPositions = getOutdatedCounterPositions(
                                equipmentGroup.positions
                              );
                              const groupCounterActionPositions = [
                                ...groupMissingPositions,
                                ...groupOutdatedPositions,
                              ];
                              const groupInvestigationLabels = getDistinctInvestigationReasonLabels(
                                equipmentGroup.positions
                              );
                              const groupPreviousCounterData = getPreviousCounterData(
                                equipmentGroup.positions
                              );
                              const groupUnverifiedDetails = getUnverifiedCounterDetails(
                                equipmentGroup.positions
                              );
                              const groupUnverifiedIds = getUnverifiedCounterIds(
                                equipmentGroup.positions
                              );
                              const hasGroupContent =
                                groupInvestigationLabels.length > 0 ||
                                groupPreviousCounterData.length > 0 ||
                                groupCounterActionPositions.length > 0 ||
                                groupUnverifiedDetails.length > 0;

                              if (!hasGroupContent) {
                                return null;
                              }

                              return (
                                <Stack
                                  key={`${settlement.id}-${equipmentGroup.equipmentId}`}
                                  spacing={1.5}
                                  sx={{
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {equipmentGroup.equipmentName}
                                  </Typography>

                                  {groupPreviousCounterData.length > 0 && (
                                    <Paper
                                      variant="outlined"
                                      className={styles.previousCounterSection}
                                    >
                                      <CounterDetailsTable
                                        title={t("Previous counter")}
                                        data={groupPreviousCounterData}
                                        showEditButton
                                        onEdit={(counterId) => setEditingCounterId(counterId)}
                                      />
                                    </Paper>
                                  )}

                                  {(groupPreviousCounterData.length > 0 &&
                                    (groupInvestigationLabels.length > 0 ||
                                      groupCounterActionPositions.length > 0 ||
                                      groupUnverifiedDetails.length > 0)) && <Divider />}

                                  {groupInvestigationLabels.length > 0 && (
                                    <Stack spacing={1}>
                                      <Typography variant="subtitle2">
                                        {t("Actions needed")}
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        className={styles.counterActionStack}
                                      >
                                        {groupInvestigationLabels.map((reasonLabel) => (
                                          <Chip
                                            key={`${settlement.id}-${equipmentGroup.equipmentId}-${reasonLabel}`}
                                            label={reasonLabel}
                                            size="small"
                                          />
                                        ))}
                                      </Stack>
                                    </Stack>
                                  )}

                                  {groupCounterActionPositions.length > 0 && (
                                    <Stack spacing={1} alignItems="flex-start">
                                      {groupCounterActionPositions.map((position) =>
                                        renderCounterInput(settlement, position)
                                      )}
                                    </Stack>
                                  )}

                                  {groupUnverifiedDetails.length > 0 && (
                                    <CounterDetailsTable
                                      data={getUnverifiedCounterTableData(groupUnverifiedDetails)}
                                      showEditButton={true}
                                      onEdit={(counterId) => setEditingCounterId(counterId)}
                                    />
                                  )}

                                  {groupUnverifiedIds.length > 0 && (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      className={styles.counterActionStack}
                                    >
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={verifyingCounters}
                                        onClick={() =>
                                          handleVerifyCounters(settlement.id, groupUnverifiedIds)
                                        }
                                      >
                                        {verifyingCounters
                                          ? t("Processing...")
                                          : t("Verify all")}
                                      </Button>
                                    </Stack>
                                  )}
                                </Stack>
                              );
                            })}
                            {bulkSaveButtons}
                          </Stack>
                        ) : (
                          <>
                            {counterActionPositions.length > 0 && (
                              <Stack spacing={1} alignItems="flex-start">
                                {counterActionPositions.map((position) =>
                                  renderCounterInput(settlement, position)
                                )}
                              </Stack>
                            )}

                            {bulkSaveButtons}

                            {unverifiedCounterDetails.length > 0 && (
                              <CounterDetailsTable
                                data={getUnverifiedCounterTableData(unverifiedCounterDetails)}
                                showEditButton={true}
                                onEdit={(counterId) => setEditingCounterId(counterId)}
                              />
                            )}

                            <Stack direction="row" spacing={1} className={styles.counterActionStack}>
                              {unverifiedCounterIds.length > 0 && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={verifyingCounters}
                                  onClick={() =>
                                    handleVerifyCounters(settlement.id, unverifiedCounterIds)
                                  }
                                >
                                  {verifyingCounters ? t("Processing...") : t("Verify all")}
                                </Button>
                              )}
                            </Stack>
                          </>
                        )}

                        {readyToConfirm && (
                          <Stack spacing={1}>
                            <Typography variant="subtitle2">
                              {t("Confirm settlement")}
                            </Typography>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "stretch", sm: "center" }}
                            >
                              <CustomDatePicker
                                label={t("Sale date")}
                                value={dayjs(
                                  confirmValues[settlement.id]?.saleDate || defaultSaleDate
                                )}
                                onChange={(value: Dayjs | null) =>
                                  handleConfirmValueChange(
                                    settlement.id,
                                    "saleDate",
                                    value ? value.format("YYYY-MM-DD") : ""
                                  )
                                }
                                slotProps={{
                                  textField: {
                                    size: "small",
                                  },
                                }}
                              />
                              <TextField
                                size="small"
                                label={t("Total Overridden")}
                                value={confirmValues[settlement.id]?.totalOverridden || ""}
                                onChange={(event) =>
                                  handleConfirmValueChange(
                                    settlement.id,
                                    "totalOverridden",
                                    event.target.value
                                  )
                                }
                              />
                              <Button
                                variant="contained"
                                size="small"
                                disabled={confirmSubmitting[settlement.id]}
                                onClick={() => handleConfirmSettlement(settlement)}
                              >
                                {confirmSubmitting[settlement.id]
                                  ? t("Processing...")
                                  : t("Confirm")}
                              </Button>
                            </Stack>
                          </Stack>
                        )}


                        <Accordion
                          disableGutters
                          elevation={0}
                          className={styles.extraPositionsAccordion}
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: "8px !important",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">{t("Extra positions")}</Typography>
                          </AccordionSummary>
                          <AccordionDetails className={styles.extraPositionsAccordionDetails}>
                            <Stack spacing={1}>
                              {getExtraPositions(settlement.id).map((position) => (
                                <Stack
                                  key={position.tempId}
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1}
                                  alignItems={{ xs: "stretch", sm: "center" }}
                                  className={styles.extraPositionRow}
                                >
                                  <TextField
                                    label={t("Position name")}
                                    size="small"
                                    value={position.name}
                                    onChange={(event) =>
                                      handleExtraPositionValueChange(
                                        settlement.id,
                                        position.tempId,
                                        "name",
                                        event.target.value
                                      )
                                    }
                                    fullWidth
                                  />
                                  <TextField
                                    label={t("Position price")}
                                    size="small"
                                    value={position.price}
                                    onChange={(event) =>
                                      handleExtraPositionValueChange(
                                        settlement.id,
                                        position.tempId,
                                        "price",
                                        event.target.value
                                      )
                                    }
                                    inputProps={{ inputMode: "decimal" }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleRemoveExtraPositionRow(
                                        settlement.id,
                                        position.tempId
                                      )
                                    }
                                  >
                                    <RemoveCircleOutlineIcon />
                                  </IconButton>
                                </Stack>
                              ))}

                              {extraPositionErrors[settlement.id] && (
                                <Typography variant="caption" color="error">
                                  {extraPositionErrors[settlement.id]}
                                </Typography>
                              )}

                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => handleAddExtraPositionRow(settlement.id)}
                                >
                                  {t("Add position")}
                                </Button>

                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={extraPositionSubmitting[settlement.id]}
                                  onClick={() => handleSaveExtraPositions(settlement)}
                                >
                                  {extraPositionSubmitting[settlement.id]
                                    ? t("Processing...")
                                    : t("Save positions")}
                                </Button>
                              </Stack>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </Stack>
                    </Paper>
                  </Stack>
                </Paper>
              );
            })}

            {hasMorePending && <Box ref={loadMoreTriggerRef} sx={{ height: 1 }} />}
            {loadingPendingMore && (
              <Stack alignItems="center" py={1}>
                <CircularProgress size={24} />
              </Stack>
            )}
          </Stack>
          </Box>
        )}
      </Box>
      )}

      <LowCounterValueConfirmModal
        opened={lowValueConfirmOpen}
        setOpened={setLowValueConfirmOpen}
        warnings={lowValueWarnings}
        onConfirm={handleConfirmLowValueBulkAdd}
      />

      <CustomModal
        opened={Boolean(editingCounterId)}
        setOpened={(value) => {
          if (!value) {
            setEditingCounterId(null);
          }
        }}
        title={t("Edit counter value")}
        width="min(480px, 92vw)"
        content={
          editingCounterId ? (
            <EquipmentCounterForm
              id={editingCounterId}
              hiddenFields={["equipment_id", "job", "kind"]}
              onSuccess={() => {
                setEditingCounterId(null);
                window.dispatchEvent(new Event("equipmentCounterAdded"));
              }}
            />
          ) : (
            <></>
          )
        }
      />
    </Box>
  );
};
